import React, { useEffect, useState } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { SVGCursorProps } from '../../types/SVGCursorProps';
import { Calculations } from '../../utils/calculations';
import { useStore } from 'zustand';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { getAdjustedCursorPosition } from './takeoffHelper';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { Point } from '../../types/Point';

export const pixelResolution = 72;

// Interfaces to handle dragging functionality
interface DraggedTick {
  index: number;
  isStart: boolean;
  startX: number;
  startY: number;
  originalPoint: Point;
}
// Update the props interface for SVGMeasurement
interface SVGMeasurementProps extends SVGCursorProps {
  isViewOnlyMode?: boolean;
}

export const SVGMeasurement: React.FC<SVGMeasurementProps> = ({
  cursorPositionRef,
  isViewOnlyMode = false,
}) => {
  const measuredPoints = useStore(useUnityBuildStore).measuredPoints;
  const setMeasuredPoints = useStore(useUnityBuildStore).setMeasuredPoints;
  const temporaryPoints = useStore(useUnityBuildStore).temporaryPoints;
  const setTemporaryPoints = useStore(useUnityBuildStore).setTemporaryPoints;
  const userScale = useStore(useUnityBuildStore).userScale;
  const isMeasuring = useStore(useKeyBoardEventStore).isMeasuring;
  const angleOptions = useStore(useUnityBuildStore).angleOptions;
  const snapMode = useStore(useKeyBoardEventStore).snapMode;
  const currentTakeoffAssembly = useStore(useAssemblyStore).currentTakeoffAssembly;

  // State for dragging functionality
  const [draggedTick, setDraggedTick] = useState<DraggedTick | null>(null);

  // Create a proper Point object
  const createPointObject = (x: number, y: number): Point => {
    return {
      pointid: `measurement-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x,
      y,
      angle: 0,
      verticallength: 0,
      allowtrailingline: true,
      isincludedincount: true,
      isselected: false,
      isactive: true,
    };
  };

  // Handle tick dragging - on mouse down
  const handleTickMouseDown = (e: React.MouseEvent, index: number, isStart: boolean) => {
    // Skip handling if in view-only mode
    if (isViewOnlyMode) return;

    e.stopPropagation();
    e.preventDefault();

    // Get the point being dragged
    const pointIndex = isStart ? index : index + 1;
    const point = measuredPoints[pointIndex];

    // Setup drag state
    setDraggedTick({
      index,
      isStart,
      startX: e.clientX,
      startY: e.clientY,
      originalPoint: { ...point } as Point,
    });

    // Add global event listeners
    window.addEventListener('mousemove', handleTickMouseMove);
    window.addEventListener('mouseup', handleTickMouseUp);
  };

  // Handle tick dragging - on mouse move
  const handleTickMouseMove = (e: MouseEvent) => {
    if (!draggedTick) return;

    // Get SVG coordinates
    const svgElement = document.getElementById('svgCanvas');
    if (!svgElement || !(svgElement instanceof SVGSVGElement)) return;

    const point = svgElement.createSVGPoint();
    point.x = e.clientX;
    point.y = e.clientY;

    const ctm = svgElement.getScreenCTM()?.inverse();
    if (!ctm) return;

    const cursorPoint = point.matrixTransform(ctm);

    // Calculate the anchor point (the opposite end of the line)
    const pointIndex = draggedTick.isStart ? draggedTick.index : draggedTick.index + 1;
    const anchorIndex = draggedTick.isStart ? draggedTick.index + 1 : draggedTick.index;

    if (anchorIndex >= measuredPoints.length) return;

    const anchorPoint = measuredPoints[anchorIndex];

    // Get adjusted cursor position based on angle snap
    // Always pass null for currentTakeoffAssembly to ensure we don't interact with assembly
    const adjustedCursor = getAdjustedCursorPosition(
      anchorPoint,
      cursorPoint,
      angleOptions,
      snapMode,
      null // Force to null to prevent any interaction with takeoff assembly
    );

    // Create a new array with the updated point
    const newPoints = [...measuredPoints];
    newPoints[pointIndex] = {
      ...newPoints[pointIndex],
      x: adjustedCursor.x,
      y: adjustedCursor.y,
    };

    setMeasuredPoints(newPoints);
  };

  // Handle tick dragging - on mouse up
  const handleTickMouseUp = () => {
    setDraggedTick(null);

    // Remove global event listeners
    window.removeEventListener('mousemove', handleTickMouseMove);
    window.removeEventListener('mouseup', handleTickMouseUp);
  };

  // This effect overrides the mouse down handler in TakeoffSVG
  useEffect(() => {
    // In view-only mode, don't set up any event handlers
    if (isViewOnlyMode) return;

    // Don't completely disable handlers when currentTakeoffAssembly exists
    // We still want to allow measurements to be visible and just prevent interaction

    const handleMouseDown = (e: MouseEvent) => {
      // Only process for measuring when isMeasuring is true, but allow interactions otherwise
      if (e.button !== 0) return;
      if (!isMeasuring) return;
      // Skip measurement interaction when working with a current assembly
      if (currentTakeoffAssembly) return;
      e.stopPropagation();

      // Get the calculated adjusted cursor position
      let adjustedCursor;

      // Special case for the first point
      if (measuredPoints.length === 0 && temporaryPoints.length === 0) {
        // When starting a new measurement, there's no reference point
        // We can't snap to an angle, so just use the current cursor position
        adjustedCursor = { ...cursorPositionRef.current };

        // Create and add the first point
        const newPoint = createPointObject(adjustedCursor.x, adjustedCursor.y);
        setMeasuredPoints([newPoint]);
        return;
      }

      // For subsequent points, we need to respect the angle snap
      const lastPoint =
        temporaryPoints.length > 0
          ? temporaryPoints[temporaryPoints.length - 1]
          : measuredPoints[measuredPoints.length - 1];

      // Pass null for currentTakeoffAssembly to ensure we don't interact with assembly
      adjustedCursor = getAdjustedCursorPosition(
        lastPoint,
        cursorPositionRef.current,
        angleOptions,
        snapMode,
        null // Force to null to prevent any interaction with takeoff assembly
      );

      // Use the adjusted cursor position instead of raw cursor position
      const newPoint = createPointObject(adjustedCursor.x, adjustedCursor.y);

      if (temporaryPoints.length > 0) {
        // Add the temporary point to measured points
        setMeasuredPoints([...measuredPoints, ...temporaryPoints, newPoint]);
        setTemporaryPoints([]);
      } else {
        // Add to existing measurement line without creating temporary points
        setMeasuredPoints([...measuredPoints, newPoint]);
      }
    };

    const svgElement = document.getElementById('svgCanvas');
    if (!svgElement) return;

    // Save original handler
    const originalMouseDown = svgElement.onmousedown;

    // Override mousedown event
    svgElement.onmousedown = e => {
      // If we're working with an assembly, don't let measurement interfere
      if (currentTakeoffAssembly) {
        // Call our handler first (which will just return)
        handleMouseDown(e as unknown as MouseEvent);

        // Then pass through to the original handler
        if (originalMouseDown) {
          // @ts-ignore
          return originalMouseDown(e);
        }
        return;
      }

      // Call our handler first
      handleMouseDown(e as unknown as MouseEvent);

      // Prevent default TakeoffSVG handler if we're measuring
      if (isMeasuring) {
        e.stopPropagation();
        e.preventDefault();
        return false;
      }

      // Otherwise, call the original handler if it exists
      if (originalMouseDown) {
        // @ts-ignore
        return originalMouseDown(e);
      }
    };

    // Cleanup
    return () => {
      if (svgElement) {
        svgElement.onmousedown = originalMouseDown;
      }
    };
  }, [
    isMeasuring,
    measuredPoints,
    temporaryPoints,
    angleOptions,
    snapMode,
    cursorPositionRef,
    currentTakeoffAssembly,
    setMeasuredPoints,
    setTemporaryPoints,
    isViewOnlyMode,
  ]);

  // Enhanced tick point generation with interactive tick marks
  const generateTickPoints = (startPoint: any, endPoint: any) => {
    if (!startPoint || !endPoint || isNaN(startPoint.x) || isNaN(endPoint.x)) {
      return [];
    }
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const angle = Math.atan2(dy, dx);
    const tickLength = 20;
    const tickOffset = -10;
    const perpendicularAngle = angle - Math.PI / 4;

    return [
      {
        x1: startPoint.x + tickOffset * Math.cos(perpendicularAngle),
        y1: startPoint.y + tickOffset * Math.sin(perpendicularAngle),
        x2: startPoint.x + (tickOffset + tickLength) * Math.cos(perpendicularAngle),
        y2: startPoint.y + (tickOffset + tickLength) * Math.sin(perpendicularAngle),
        isStart: true,
      },
      {
        x1: endPoint.x + tickOffset * Math.cos(perpendicularAngle),
        y1: endPoint.y + tickOffset * Math.sin(perpendicularAngle),
        x2: endPoint.x + (tickOffset + tickLength) * Math.cos(perpendicularAngle),
        y2: endPoint.y + (tickOffset + tickLength) * Math.sin(perpendicularAngle),
        isStart: false,
      },
    ];
  };

  // Enhanced measurement line renderer with draggable ticks
  const renderMeasurementLine = (
    startPoint: any,
    endPoint: any,
    index: number,
    color: string = 'black',
    isDraggable: boolean = true
  ) => {
    const pixelDistance = Calculations.calculateDistance(startPoint, endPoint);
    const inchDistance = pixelDistance / pixelResolution;
    const realWorldDistanceInFeet = inchDistance * userScale;
    const feet = Math.floor(realWorldDistanceInFeet);
    const inches = Math.round((realWorldDistanceInFeet - feet) * 12);

    const ticks = generateTickPoints(startPoint, endPoint);

    return (
      <>
        <line
          x1={startPoint.x}
          y1={startPoint.y}
          x2={endPoint.x}
          y2={endPoint.y}
          stroke={color}
          strokeWidth={2}
          strokeDasharray={color === 'black' ? '' : '5,5'}
        />
        <rect
          x={(startPoint.x + endPoint.x) / 2 - 20}
          y={(startPoint.y + endPoint.y) / 2 - 15}
          width="40"
          height="12"
          fill="white"
          opacity={0.75}
          strokeWidth="1"
        />
        <text
          x={(startPoint.x + endPoint.x) / 2}
          y={(startPoint.y + endPoint.y) / 2 - 5}
          fill={color}
          fontSize="12"
          textAnchor="middle"
        >
          {`${feet}' ${inches}"`}
        </text>
        {ticks.map((tick, i) => (
          <g key={`tick-${index}-${i}`}>
            <line
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke={color}
              strokeWidth={2}
              pointerEvents="none"
            />
            {isDraggable && !isViewOnlyMode && (
              <circle
                cx={tick.isStart ? startPoint.x : endPoint.x}
                cy={tick.isStart ? startPoint.y : endPoint.y}
                r={8}
                fill="transparent"
                stroke={color === 'black' ? '#4285F4' : color}
                strokeWidth={1}
                strokeDasharray="2,2"
                style={{ cursor: 'move' }}
                onMouseDown={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleTickMouseDown(e, index, tick.isStart);
                }}
                pointerEvents="all"
              />
            )}
          </g>
        ))}
      </>
    );
  };

  return (
    <g>
      {/* Always render completed measurements regardless of isMeasuring or currentTakeoffAssembly state */}
      {measuredPoints.map((point, i) => {
        if (i < measuredPoints.length - 1) {
          return (
            <React.Fragment key={`measurement-${i}`}>
              {renderMeasurementLine(point, measuredPoints[i + 1], i)}
            </React.Fragment>
          );
        }
        return null;
      })}

      {/* Live measurement as user moves cursor - only when actively measuring */}
      {isMeasuring &&
        !isViewOnlyMode && // Don't show live measurement in view-only mode
        !currentTakeoffAssembly && // Don't show the temporary line when working with an assembly
        (measuredPoints.length > 0 || temporaryPoints.length > 0) &&
        cursorPositionRef.current?.x !== undefined &&
        cursorPositionRef.current?.y !== undefined && (
          <>
            {(() => {
              const lastPoint =
                temporaryPoints.length > 0
                  ? temporaryPoints[temporaryPoints.length - 1]
                  : measuredPoints[measuredPoints.length - 1];

              // Pass null instead of currentTakeoffAssembly to ensure complete isolation
              const adjustedCursor = getAdjustedCursorPosition(
                lastPoint,
                cursorPositionRef.current,
                angleOptions,
                snapMode,
                null // Force to null to prevent any interaction with takeoff assembly
              );

              return (
                <>
                  <line
                    x1={lastPoint.x}
                    y1={lastPoint.y}
                    x2={adjustedCursor.x}
                    y2={adjustedCursor.y}
                    stroke="red"
                    strokeWidth={2}
                    strokeDasharray="5,5"
                    className="temporary-measurement-line"
                    pointerEvents="none"
                  />
                  <rect
                    x={(lastPoint.x + adjustedCursor.x) / 2 - 20}
                    y={(lastPoint.y + adjustedCursor.y) / 2 - 15}
                    width="40"
                    height="12"
                    fill="white"
                    opacity={0.75}
                    strokeWidth="1"
                    pointerEvents="none"
                  />
                  <text
                    x={(lastPoint.x + adjustedCursor.x) / 2}
                    y={(lastPoint.y + adjustedCursor.y) / 2 - 5}
                    fill="red"
                    fontSize="12"
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    {(() => {
                      const pixelDistance = Calculations.calculateDistance(
                        lastPoint,
                        adjustedCursor
                      );
                      const inchDistance = pixelDistance / pixelResolution;
                      const realWorldDistanceInFeet = inchDistance * userScale;
                      const feet = Math.floor(realWorldDistanceInFeet);
                      const inches = Math.round((realWorldDistanceInFeet - feet) * 12);
                      return `${feet}' ${inches}"`;
                    })()}
                  </text>

                  {generateTickPoints(lastPoint, adjustedCursor).map((tick, i) =>
                    tick.x1 && tick.y1 && tick.x2 && tick.y2 ? (
                      <line
                        key={i}
                        x1={tick.x1}
                        y1={tick.y1}
                        x2={tick.x2}
                        y2={tick.y2}
                        stroke="red"
                        strokeWidth={2}
                        pointerEvents="none"
                      />
                    ) : null
                  )}
                </>
              );
            })()}
          </>
        )}
    </g>
  );
};
